import { useEffect, useRef } from "react";
import { Icon, IconButton, Typography, SidePanel, Skeleton } from "@/components/atoms";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";
import { JobHistorySearchFilters } from "./JobHistorySearchFilters";
import { JobHistoryTable } from "./JobHistoryTable";
import { useInfiniteJobHistoryLogs } from "./useInfiniteJobHistoryLogs";
import { useJobHistorySearchFilters } from "./useJobHistorySearchFilters";

interface JobHistoryProps {
  open: boolean;
  onClose: () => void;
}

export const JobHistory = ({ open, onClose }: JobHistoryProps) => {
  const { id: bookingId, jobNumber } = useSelectedBooking();
  const searchFiltersContext = useJobHistorySearchFilters();
  const { search, setSearch, filters, setFilters } = searchFiltersContext;
  const activeFilters = search ? [] : filters;
  const { isFetching: isLogsLoading, ref, getBookingHistoryLogs, filterParams, searchParam, logs } = useInfiniteJobHistoryLogs({ filters: activeFilters, search });
  const isRefreshing = useRef(false);

  useEffect(() => {
    if (open) {
      isRefreshing.current = true;
      getBookingHistoryLogs({ id: bookingId, page: "1", ...filterParams, search: searchParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterParams, searchParam]);

  useEffect(() => {
    if (!isLogsLoading) isRefreshing.current = false;
  }, [isLogsLoading]);

  const handleClose = () => {
    setSearch("");
    setFilters([]);
    onClose();
  };

  const showFullSkeleton = isRefreshing.current && isLogsLoading;

  return (
    <SidePanel open={open} onClose={handleClose} width={800} focused ref={ref}>
      <section className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Icon name="CalendarCircle" size="lg" />
          <Typography variant="title">Job History ({jobNumber})</Typography>
        </div>
        <div className="ml-3 flex h-7 items-center">
          <IconButton iconName="ArrowRight" onClick={handleClose} className="-mr-2" variant="custom" />
        </div>
      </section>
      <section className="flex flex-col p-5">
        <JobHistorySearchFilters searchFiltersContext={searchFiltersContext} />
        {showFullSkeleton ? <Skeleton /> : <JobHistoryTable logs={logs} />}
        {isLogsLoading && !showFullSkeleton && <Skeleton variant="short" />}
      </section>
    </SidePanel>
  );
};
